<template>
  <div class="gallery">
    <div class="gallery__header gallery__columns">
      <div class="gallery__column">
        <p class="gallery__heading" :style="themeStyles">Gallery</p>
        <p
          class="gallery__episode-count"
          :class="`gallery__episode-count--${this.theme.type}`"
          :style="episodeCountStyles"
        >
          {{ episodeCountText }}
        </p>
      </div>
      <div class="gallery__column flex-end">
        <button
          class="gallery__button"
          :class="`gallery__button--${theme.type}`"
          @click="handleSearchClick"
        >
          <span class="is-sr-only">Search</span>
          <span
            v-html="searchIconSvg"
            class="gallery__svg gallery__svg--search"
          ></span>
        </button>

        <button
          class="gallery__button"
          :class="`gallery__button--${theme.type}`"
          @click="handleSortClick"
        >
          <span class="is-sr-only">Sort {{ nextSortAction }}</span>
          <span v-html="sortSvg" class="gallery__svg gallery__svg--sort"></span>
        </button>
      </div>
    </div>
    <search-actions-bar
      :is-visible="showingFilteredPodcasts"
      :search-term="searchTerm"
      :handle-clear-search="handleClearSearch"
      :theme="theme"
    ></search-actions-bar>
    <div class="gallery__body">
      <div class="gallery__pages">
        <!-- LP-3 (MR-77) reversed-`v-for` fix: Vue1's `(i, page)` bound
             i=INDEX, page=VALUE (index-first). Vue3 is (value, index), so the
             alias order is flipped to keep `i` the real 0-based page index and
             `page` the sub-array — preserving `:key`/`:id`/active/`item in page`
             to match the Webpack baseline's rendered output. -->
        <div
          v-for="(page, i) in paginatedGalleryItems"
          :key="i"
          :id="`gallery__page--${i}`"
          class="gallery__page"
          :class="{ 'gallery__page--active': i === currentPage }"
        >
          <gallery-item
            v-for="item in page"
            :key="item.id"
            :item="item"
            :is-playing="isPlaying && item.id === podcastId"
            :is-loading="isLoading && item.id === podcastId"
            :is-classic="isClassic"
            :theme="theme"
            @play="handlePlay"
            @pause="$emit('pause')"
          ></gallery-item>
        </div>
      </div>
    </div>
    <div class="gallery__footer">
      <pagination
        :count="paginatedGalleryItems.length"
        :current-page="currentPage"
        :theme="theme"
        @change-page="changePage"
        ref="pagination"
      ></pagination>
    </div>
  </div>
</template>

<script>
import Item from './Item';
import SearchActionsBar from './SearchActionsBar';
import Pagination from './Pagination';

export default {
  name: 'gallery',
  // Vue3 (LP-4/MR-78 emits parity): declare the $emitted events (incl. the
  // template `@pause="$emit('pause')"` re-emit) so the parent Player's listeners
  // bind as component events, not native-attr fallthrough.
  emits: ['load-podcast', 'pause'],
  components: {
    'gallery-item': Item,
    SearchActionsBar,
    Pagination,
  },
  props: {
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    isClassic: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isPlaying: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isLoading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    podcastId: Number,
    handleSearchClick: {
      type: Function,
      default: () => {},
    },
    showingFilteredPodcasts: {
      type: Boolean,
      default: false,
    },
    handleClearSearch: {
      type: Function,
      default: () => {},
    },
    searchTerm: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      sortMode: 'desc', // 'desc' || 'asc'
      currentPage: 0,
    };
  },
  computed: {
    nextSortAction() {
      return this.sortMode === 'desc' ? 'Ascending' : 'Descending';
    },

    searchIconSvg() {
      const fill = this.getFillColor();
      return `
        <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12.5 11.9937H11.71L11.43 11.7237C12.41 10.5837 13 9.10365 13 7.49365C13 3.90365 10.09 0.993652 6.5
            0.993652C2.91 0.993652 0 3.90365 0 7.49365C0 11.0837 2.91 13.9937 6.5 13.9937C8.11 13.9937 9.59 13.4037
            10.73 12.4237L11 12.7037V13.4937L16 18.4837L17.49 16.9937L12.5 11.9937ZM6.5 11.9937C4.01 11.9937 2 9.98365 2
            7.49365C2 5.00365 4.01 2.99365 6.5 2.99365C8.99 2.99365 11 5.00365 11 7.49365C11 9.98365 8.99 11.9937 6.5
            11.9937Z"
            fill="${fill}"
          />
        </svg>`;
    },

    sortSvg() {
      const fill = this.getFillColor();
      return `
        <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M7.00012 0.993652C6.72824 0.993652 6.45567 1.09625 6.24817 1.30225L0.312618 7.19287C-0.356382 7.85687
            0.117571 8.99365 1.06457 8.99365H12.9357C13.8827 8.99365 14.3576 7.85687 13.6876 7.19287L7.75207
            1.30225C7.54407 1.09625 7.27199 0.993652 7.00012 0.993652ZM1.06457 10.9937C0.117571 10.9937 -0.355429
            12.1304 0.314571 12.7944L6.24817 18.6851C6.66317 19.0971 7.33707 19.0971 7.75207 18.6851L13.6857
            12.7944C14.3547 12.1304 13.8817 10.9937 12.9357 10.9937H1.06457Z"
            fill="${fill}"
          />
        </svg>`;
    },

    episodeCountText() {
      const length = this.items.length || 0;
      const episodes = length === 1 ? 'Episode' : 'Episodes';
      return `${length} ${episodes}`;
    },

    themeStyles() {
      return {
        color: this.theme.text,
      };
    },

    episodeCountStyles() {
      if (this.theme.type !== 'custom') {
        return null;
      }

      return {
        color: this.theme.text,
      };
    },

    /**
     * Pages containing gallery items
     *
     * @return {Array}
     */
    paginatedGalleryItems() {
      // Splice mutates; copy first so we don't drain the cached
      // computed result of sortedGalleryItems on first read. Without
      // the copy the second access returns pages built from an empty
      // array, which the podcastId watcher then renders as nothing.
      const items = [...this.sortedGalleryItems];
      const pages = [];

      while (items.length >= 1) {
        pages.push(items.splice(0, 3));
      }
      return pages;
    },

    sortedGalleryItems() {
      const galleryItems = [...this.items];
      if (this.sortMode === 'asc') {
        return galleryItems.reverse();
      }
      return galleryItems;
    },
  },
  watch: {
    /**
     * A filtered list (a search, a category change) can be shorter than the page the
     * user is on. Nothing re-clamped it, so the gallery rendered an empty page —
     * the "results are invisible after a search" symptom Phase 2 reported.
     */
    paginatedGalleryItems(pages) {
      if (this.currentPage > pages.length - 1) {
        this.changePage(0);
      }
    },

    /**
     * listen to the current playing podcast
     * in order to trigger a page change if that
     * pod exists on a different page
     */
    podcastId() {
      const pageContainingCurrentPodcast = this.paginatedGalleryItems.findIndex((page) => {
        return page.find((pod) => pod.id === this.podcastId);
      });
      this.changePage(pageContainingCurrentPodcast);
    },
  },
  methods: {
    handleSortClick() {
      this.sortMode = this.sortMode === 'desc' ? 'asc' : 'desc';
      this.$nextTick(() => {
        this.changePage(0); // force user back to page 1
      });
    },

    getFillColor() {
      let fill;
      switch (this.theme.type) {
        case 'dark':
          fill = '#ffffff';
          break;

        case 'custom':
          fill = this.theme.text;
          break;

        default:
          fill = '#222046'; // default is light mode
      }
      return fill;
    },

    handlePlay(event) {
      this.$emit('load-podcast', event);
    },

    /**
     * Changes the page
     *
     * @param {Number} page the page to change to -- zero based index
     * @return {void}
     */
    changePage(page) {
      // 🔑 The active page is a RENDERED consequence of `currentPage` now, not a class
      // moved by hand. This used to do `this.$el.querySelector('#gallery__page--N')
      // .classList` with no null guard, against a binding that said page 0 was active
      // — two writers for one attribute, and the querySelector returns null for any
      // page that does not exist:
      //   - `changePage(99)` (or any stale index) threw a TypeError;
      //   - the `podcastId` watcher passes `findIndex(...)`, which is **-1** when the
      //     playing episode is not in the list — exactly what a search excluding it
      //     produces — so it threw on every such search, inside a Vue watcher where
      //     the throw is swallowed and the page silently stops updating.
      // Found by Phase 2; the same lesson as PlayerInfo's marquee in this branch.
      const pages = this.paginatedGalleryItems.length;
      // Clamp. A filtered list can be shorter than the page the user was on, and
      // `handleSortClick` already knew a list change needs this — it calls
      // `changePage(0)` with the comment "force user back to page 1". A search had no
      // equivalent, so `currentPage` could point past the end and render nothing.
      this.currentPage = pages === 0 ? 0 : Math.min(Math.max(page, 0), pages - 1);
      if (this.$refs.pagination) {
        this.$refs.pagination.showMorePages = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/variables-v2';
@import '../../assets/_helpers';

.gallery {
  font-family: $font;
  margin-bottom: 1rem;
  &__columns {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 -0.75rem;
  }

  &__column {
    flex: 1 0 auto;
    padding: 0 0.75rem;
  }

  &__header {
    margin-bottom: 1rem;
  }

  &__heading {
    display: inline-block;
    color: #222046;
    font-weight: 500;
    font-size: 1.25rem;
  }

  &__episode-count {
    display: inline-block;
    padding-left: 2px;
    &--light {
      color: #6e6e7a;
    }
    &--dark {
      color: #f5f5f5;
    }
  }

  &__button {
    background: transparent;
    border: 0;
    padding: 5px;
    margin-left: 0.75rem;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    cursor: pointer;
    &--light {
      &:hover,
      &:focus:not(:active) {
        opacity: 0.8;
        background: #e3e3e3;
      }
    }
    &--dark {
      &:hover,
      &:focus:not(:active) {
        opacity: 0.8;
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__svg {
    display: block;
    &--search {
      transform: translateY(2px); // added for optical balance
    }
    &--sort {
      transform: translate(1px, 2px); // added to center
    }
  }

  &__page {
    display: none;
    visibility: hidden;
    &--active {
      display: block;
      visibility: visible;
    }
  }

  &__body {
    margin-bottom: 1.5rem;
    min-height: 180px;
  }
}
</style>
